import type React from 'react';

import MuiCollapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';

import { useBooleanState } from '../../hooks/useBooleanState';
import { Iconify } from '../Iconify';

export interface CollapsibleInfoProps {
  /** on the "i" button that opens and closes the text */
  'data-testid'?: string;
  triggerPaddingRight?: number | string;
  children: React.ReactNode;
  trigger: React.ReactNode;
  defaultOpen?: boolean;
  fullWidth?: boolean;
}

export const CollapsibleInfo = ({
  'data-testid': testId,
  children,
  defaultOpen = false,
  fullWidth = false,
  trigger,
  triggerPaddingRight = '24px',
}: CollapsibleInfoProps) => {
  const show = useBooleanState(defaultOpen);

  if (fullWidth) {
    return (
      <Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ minWidth: 0 }}>{trigger}</Box>
          <Box
            sx={(theme) => ({
              alignItems: 'center',
              backgroundColor: show.isActive
                ? alpha(theme.palette.primary.main, 0.1)
                : 'transparent',
              border: 0,
              borderRadius: '50%',
              color: 'text.secondary',
              cursor: 'pointer',
              display: 'inline-flex',
              flexShrink: 0,
              height: 32,
              justifyContent: 'center',
              p: 0,
              transition: 'all 0.15s',
              width: 32,
            })}
            aria-expanded={show.isActive}
            data-testid={testId}
            onClick={show.toggle}
            component="button"
            type="button"
          >
            <Iconify icon="mdi:information-outline" size="small" />
          </Box>
        </Box>
        <MuiCollapse in={show.isActive}>{children}</MuiCollapse>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        paddingRight={triggerPaddingRight}
        width={'fit-content'}
        position={'relative'}
      >
        {trigger}
        <Box
          sx={(theme) => ({
            backgroundColor: show.isActive
              ? alpha(theme.palette.primary.main, 0.1)
              : 'transparent',
            borderColor: 'text.secondary',
            color: 'text.secondary',
            cursor: 'pointer',
            padding: 0,
            transition: 'all 0.15s',
          })}
          justifyContent={'center'}
          display={'inline-flex'}
          aria-expanded={show.isActive}
          data-testid={testId}
          onClick={show.toggle}
          position={'absolute'}
          alignItems={'center'}
          component="button"
          type="button"
          height={18}
          width={18}
          right={0}
          top={0}
        >
          <Iconify icon="mdi:information-outline" size="small" />
        </Box>
      </Box>
      <MuiCollapse in={show.isActive}>{children}</MuiCollapse>
    </Box>
  );
};
